import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Users, BookOpen, GraduationCap, Shield, Search, Trash2, Edit, 
  UserCircle, Award, FolderOpen, ArrowLeft, X, Check, Loader2 
} from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard({ user }) {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [editingUser, setEditingUser] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const navigate = useNavigate();

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    const headers = {
      'Accept': 'application/json',
      'Content-Type': 'application/json'
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    return headers;
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, usersRes] = await Promise.all([
        fetch(`${API}/admin/stats`, { headers: getAuthHeaders() }),
        fetch(`${API}/admin/users`, { headers: getAuthHeaders() })
      ]);

      if (statsRes.ok) {
        setStats(await statsRes.json());
      }
      if (usersRes.ok) {
        const data = await usersRes.json();
        setUsers(data.users);
      }
    } catch (error) {
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateUser = async (userId, updates) => {
    setActionLoading(userId);
    try {
      const response = await fetch(`${API}/admin/users/${userId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(updates)
      });

      const data = await response.json();
      
      if (response.ok) {
        toast.success('User updated successfully');
        setEditingUser(null);
        loadData();
      } else {
        toast.error(data.detail || 'Failed to update user');
      }
    } catch (error) {
      toast.error('Failed to update user');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteUser = async (userId) => {
    setActionLoading(userId);
    try {
      const response = await fetch(`${API}/admin/users/${userId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        toast.success('User deleted successfully');
        setDeleteConfirm(null);
        loadData();
      } else {
        const data = await response.json();
        toast.error(data.detail || 'Failed to delete user');
      }
    } catch (error) {
      toast.error('Failed to delete user');
    } finally {
      setActionLoading(null);
    }
  };

  const filteredUsers = users.filter(u => {
    const matchesSearch = u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         u.email.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = roleFilter === "all" || u.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  if (user?.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAEDCD]">
        <Card className="p-8 text-center bg-[#FEFAE0] border-[#CCD5AE]/50 rounded-2xl">
          <Shield className="w-16 h-16 text-[#CCD5AE] mx-auto mb-4" />
          <p className="text-[#588157] mb-4">Admin access required</p>
          <Button onClick={() => navigate('/dashboard')} className="bg-[#3A5A40] text-white hover:bg-[#344E41] rounded-lg">
            Back to Dashboard
          </Button>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#FAEDCD]">
        <div className="text-[#3A5A40] text-lg">Loading admin panel...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAEDCD]">
      <nav className="bg-[#FEFAE0] border-b border-[#CCD5AE]/50 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-[#3A5A40]" strokeWidth={1.5} />
            <h1 className="text-2xl font-bold text-[#344E41]" style={{ fontFamily: 'Merriweather, serif' }}>
              Admin Dashboard
            </h1>
          </div>
          <Button
            variant="ghost"
            onClick={() => navigate('/dashboard')}
            className="text-[#3A5A40] hover:bg-[#E9EDC9]"
          >
            <ArrowLeft className="w-5 h-5 mr-2" strokeWidth={1.5} />
            Back to Dashboard
          </Button>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto p-6 md:p-8">
        {/* Stats Grid */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="p-4 bg-[#FEFAE0] border-[#CCD5AE]/50 rounded-xl">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#3A5A40]/10 rounded-lg">
                <Users className="w-5 h-5 text-[#3A5A40]" />
              </div>
              <div>
                <p className="text-xs text-[#A3B18A]">Total Users</p>
                <p className="text-2xl font-bold text-[#344E41]">{stats?.total_users || 0}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-[#FEFAE0] border-[#CCD5AE]/50 rounded-xl">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#588157]/10 rounded-lg">
                <UserCircle className="w-5 h-5 text-[#588157]" />
              </div>
              <div>
                <p className="text-xs text-[#A3B18A]">Students</p>
                <p className="text-2xl font-bold text-[#344E41]">{stats?.total_students || 0}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-[#FEFAE0] border-[#CCD5AE]/50 rounded-xl">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#D4A373]/10 rounded-lg">
                <GraduationCap className="w-5 h-5 text-[#D4A373]" />
              </div>
              <div>
                <p className="text-xs text-[#A3B18A]">Teachers</p>
                <p className="text-2xl font-bold text-[#344E41]">{stats?.total_teachers || 0}</p>
              </div>
            </div>
          </Card>
          <Card className="p-4 bg-[#FEFAE0] border-[#CCD5AE]/50 rounded-xl">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#3A5A40]/10 rounded-lg">
                <BookOpen className="w-5 h-5 text-[#3A5A40]" />
              </div>
              <div>
                <p className="text-xs text-[#A3B18A]">Courses</p>
                <p className="text-2xl font-bold text-[#344E41]">{stats?.total_courses || 0}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Users Management */}
        <Card className="p-6 bg-[#FEFAE0] border-[#CCD5AE]/50 rounded-2xl">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
            <h2 className="text-xl font-bold text-[#344E41]" style={{ fontFamily: 'Merriweather, serif' }}>
              User Management
            </h2>
            <div className="flex gap-3">
              <div className="relative flex-1 md:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#A3B18A]" />
                <Input
                  placeholder="Search users..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  data-testid="search-users-input"
                  className="pl-9 bg-white/50 border-[#CCD5AE] rounded-lg"
                />
              </div>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="w-32 bg-white/50 border-[#CCD5AE] rounded-lg" data-testid="role-filter">
                  <SelectValue placeholder="Filter role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="student">Students</SelectItem>
                  <SelectItem value="teacher">Teachers</SelectItem>
                  <SelectItem value="admin">Admins</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Users Table */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#CCD5AE]">
                  <th className="text-left py-3 px-4 text-sm font-medium text-[#588157]">User</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-[#588157]">Email</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-[#588157]">Role</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-[#588157]">Enrollments</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-[#588157]">Joined</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-[#588157]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((u) => (
                  <tr key={u.user_id} className="border-b border-[#CCD5AE]/50 hover:bg-[#E9EDC9]/30" data-testid={`user-row-${u.user_id}`}>
                    <td className="py-3 px-4">
                      <div className="flex items-center gap-3">
                        {u.picture ? (
                          <img src={u.picture} alt={u.name} className="w-8 h-8 rounded-full object-cover" />
                        ) : (
                          <div className="w-8 h-8 rounded-full bg-[#3A5A40]/10 flex items-center justify-center">
                            <span className="text-sm font-medium text-[#3A5A40]">{u.name.charAt(0)}</span>
                          </div>
                        )}
                        {editingUser === u.user_id ? (
                          <Input
                            defaultValue={u.name}
                            id={`edit-name-${u.user_id}`}
                            className="w-32 h-8 text-sm bg-white border-[#CCD5AE]"
                          />
                        ) : (
                          <span className="font-medium text-[#344E41]">{u.name}</span>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm text-[#588157]">{u.email}</td>
                    <td className="py-3 px-4">
                      {editingUser === u.user_id ? (
                        <Select defaultValue={u.role} onValueChange={(val) => document.getElementById(`edit-role-${u.user_id}`).value = val}>
                          <SelectTrigger className="w-24 h-8 text-sm bg-white border-[#CCD5AE]">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="student">Student</SelectItem>
                            <SelectItem value="teacher">Teacher</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          u.role === 'admin' ? 'bg-[#3A5A40]/10 text-[#3A5A40]' :
                          u.role === 'teacher' ? 'bg-[#D4A373]/10 text-[#D4A373]' :
                          'bg-[#588157]/10 text-[#588157]'
                        }`}>
                          {u.role}
                        </span>
                      )}
                      <input type="hidden" id={`edit-role-${u.user_id}`} defaultValue={u.role} />
                    </td>
                    <td className="py-3 px-4 text-sm text-[#588157]">{u.enrollment_count || 0}</td>
                    <td className="py-3 px-4 text-sm text-[#588157]">
                      {u.created_at ? new Date(u.created_at).toLocaleDateString() : '-'}
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex justify-end gap-2">
                        {editingUser === u.user_id ? (
                          <>
                            <Button
                              size="sm"
                              onClick={() => {
                                const nameInput = document.getElementById(`edit-name-${u.user_id}`);
                                const roleInput = document.getElementById(`edit-role-${u.user_id}`);
                                handleUpdateUser(u.user_id, {
                                  name: nameInput?.value || u.name,
                                  role: roleInput?.value || u.role
                                });
                              }}
                              disabled={actionLoading === u.user_id}
                              className="bg-[#3A5A40] text-white hover:bg-[#344E41] h-8"
                              data-testid={`save-user-${u.user_id}`}
                            >
                              {actionLoading === u.user_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setEditingUser(null)}
                              className="text-[#588157] hover:bg-[#E9EDC9] h-8"
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          </>
                        ) : deleteConfirm === u.user_id ? (
                          <>
                            <Button
                              size="sm"
                              onClick={() => handleDeleteUser(u.user_id)}
                              disabled={actionLoading === u.user_id}
                              className="bg-red-500 text-white hover:bg-red-600 h-8"
                              data-testid={`confirm-delete-${u.user_id}`}
                            >
                              {actionLoading === u.user_id ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete'}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setDeleteConfirm(null)}
                              className="text-[#588157] hover:bg-[#E9EDC9] h-8"
                            >
                              Cancel
                            </Button>
                          </>
                        ) : (
                          <>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setEditingUser(u.user_id)}
                              className="text-[#588157] hover:bg-[#E9EDC9] h-8"
                              data-testid={`edit-user-${u.user_id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                            {u.user_id !== user.user_id && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setDeleteConfirm(u.user_id)}
                                className="text-red-500 hover:bg-red-50 h-8"
                                data-testid={`delete-user-${u.user_id}`}
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredUsers.length === 0 && (
            <div className="text-center py-8 text-[#588157]">
              No users found matching your criteria
            </div>
          )}
        </Card>
      </main>
    </div>
  );
}
